package dev.danko.roblox_craft_ai.components.layouts

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobwebx.markdown.markdown
import dev.danko.roblox_craft_ai.components.SiteFooter
import dev.danko.roblox_craft_ai.components.SiteHeader
import dev.danko.roblox_craft_ai.components.SiteSection
import dev.danko.roblox_craft_ai.uikit.i18n.strings
import dev.danko.roblox_craft_ai.uikit.theme.LocalAppTheme
import dev.danko.roblox_craft_ai.uikit.theme.toAppPalette
import kotlinx.browser.document
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div

/** Readable long-form text in style A: headings, lists, tables and links of the legal documents. */
val LegalProseStyle = CssStyle {
    base { Modifier.fillMaxWidth().fontSize(1.cssRem).lineHeight(1.65).color(colorMode.toAppPalette().textPrimary) }
    cssRule("h1") { Modifier.fontSize(2.cssRem).fontWeight(FontWeight.ExtraBold).lineHeight(1.2).margin(top = 0.5.cssRem, bottom = 0.75.cssRem) }
    cssRule("h2") { Modifier.fontSize(1.3.cssRem).fontWeight(FontWeight.Bold).margin(top = 2.cssRem, bottom = 0.5.cssRem) }
    cssRule("p") { Modifier.margin(topBottom = 0.75.cssRem).color(colorMode.toAppPalette().textSecondary) }
    cssRule("li") { Modifier.margin(bottom = 0.4.cssRem).color(colorMode.toAppPalette().textSecondary) }
    cssRule("strong") { Modifier.color(colorMode.toAppPalette().textPrimary).fontWeight(FontWeight.SemiBold) }
    cssRule("a") { Modifier.color(colorMode.toAppPalette().accentText) }
    cssRule("code") { Modifier.fontSize(0.9.cssRem).padding(leftRight = 0.3.cssRem).borderRadius(4.px).backgroundColor(colorMode.toAppPalette().surfaceElevated) }
    cssRule("table") { Modifier.fillMaxWidth().fontSize(0.9.cssRem).margin(topBottom = 1.cssRem).display(org.jetbrains.compose.web.css.DisplayStyle.Block).overflow { x(Overflow.Auto) } }
    cssRule("th") {
        Modifier.padding(0.5.cssRem).fontWeight(FontWeight.SemiBold).color(colorMode.toAppPalette().textPrimary)
            .textAlign(com.varabyte.kobweb.compose.css.TextAlign.Left)
            .borderBottom(1.px, LineStyle.Solid, colorMode.toAppPalette().borderStrong)
    }
    cssRule("td") {
        Modifier.padding(0.5.cssRem).color(colorMode.toAppPalette().textSecondary)
            .borderBottom(1.px, LineStyle.Solid, colorMode.toAppPalette().border)
    }
}

/** Layout of the Markdown documents under resources/markdown/legal (site header, text column, footer). */
@Composable
@Layout
fun LegalLayout(ctx: PageContext, content: @Composable () -> Unit) {
    val title = ctx.markdown?.frontMatter?.get("title")?.singleOrNull()
    LaunchedEffect(title) { document.title = listOfNotNull(title, "Craftiva AI").joinToString(" — ") }
    val palette = LocalAppTheme.current.colors

    SiteHeader(SiteSection.OTHER)
    Box(Modifier.fillMaxWidth(), contentAlignment = Alignment.TopCenter) {
        Column(Modifier.fillMaxWidth().maxWidth(48.cssRem).padding(leftRight = 1.cssRem, top = 1.5.cssRem)) {
            Link("/", strings().legal.backToSite, Modifier.fontSize(0.9.cssRem).color(palette.textSecondary), variant = UncoloredLinkVariant)
            Div(LegalProseStyle.toAttrs()) { content() }
        }
    }
    SiteFooter()
}
